'use client';

import { FC, MutableRefObject, useCallback, useEffect, useRef, useState } from 'react';
import { Search, MapPin, ChevronDown, X } from 'lucide-react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { useAppDispatch, useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { updateRecentSearches } from '@/hotdesk-ui/helpers/location.helper';
import SearchSuggestionsDropdown from './SearchSuggestionsDropdown';
import { useSearchAutocomplete } from '@/hotdesk-ui/hooks/useSearchAutocomplete';
import { setGeoLocation } from '@/hotdesk-ui/store/slices/geo-location.slice';
import { setCurrentSearch } from '@/hotdesk-ui/store/slices/search.slice';
import { getListingData, getWorkspaceData } from '@/hotdesk-ui/store/slices/listing.slice';
import { GeoCodeResult } from '@/hotdesk-ui/types/geo-code-result.type';
import { StorageKeys } from '@/hotdesk-ui/enum/storage-keys';
import { storeGet } from '@/hotdesk-ui/helpers/utils';
import styles from './SearchModal.module.scss';
export interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  autocompleteInput: MutableRefObject<any>;
  autocompleteContainer: MutableRefObject<any>;
  getPermission: () => void;
  sentData: (typeOverride?: number) => void;
  listingType: number;
  addFilters: (value: number) => void;
}
const SearchModal: FC<SearchModalProps> = ({
  isOpen,
  onClose,
  autocompleteInput,
  autocompleteContainer,
  getPermission,
  sentData,
  listingType,
  addFilters
}) => {
  const t = useTranslations();
  const dispatch = useAppDispatch();
  const listingTypes = useAppSelector((state: RootState) => state.listingTypes.items);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [localType, setLocalType] = useState(0);
  const [hasSearchValue, setHasSearchValue] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Place picked from the custom suggestions dropdown. Mirrors landing/explore
  // autocomplete behavior so listings refresh immediately on pick.
  const handlePlaceSelect = useCallback((placeData: GeoCodeResult) => {
    placeData.source = 'autocomplete';
    const recent = (storeGet(StorageKeys.RECENT_SEARCH) || []) as GeoCodeResult[];
    updateRecentSearches(placeData, recent);
    dispatch(setGeoLocation(placeData));
    dispatch(setCurrentSearch(placeData));
    dispatch(getWorkspaceData({
      lastSearch: placeData,
      page: 1
    }));
    dispatch(getListingData({
      lastSearch: placeData,
      page: 1
    }));
  }, [dispatch]);

  // Run the search and close up — the tail of both commit paths, whether the
  // user pressed Enter on a suggestion or hit the search button.
  const proceed = useCallback(() => {
    addFilters(localType);
    sentData(localType);
    onClose();
  }, [addFilters, sentData, onClose, localType]);
  const {
    containerRef,
    predictions,
    activeIndex,
    isOpen: suggestionsOpen,
    setActiveIndex,
    selectByIndex,
    selectActiveOrFirst,
    resetToEmpty
  } = useSearchAutocomplete({
    inputRef: autocompleteInput,
    onSelect: handlePlaceSelect,
    onEnterSelect: proceed,
    // The modal renders null while closed, so the input doesn't exist at
    // mount and the hook's listener-binding effect bails. `enabled` is in
    // that effect's deps — flipping it on open re-runs the binding once
    // the input is actually in the DOM.
    enabled: isOpen
  });
  const syncSearchValue = useCallback(() => {
    const value = autocompleteInput.current?.value?.trim() ?? '';
    setHasSearchValue(value.length > 0);
  }, [autocompleteInput]);
  const handleClearSearch = () => {
    if (autocompleteInput.current) autocompleteInput.current.value = '';
    setHasSearchValue(false);
    autocompleteInput.current?.focus();
    // Wiping the value in code fires no input event — drop the predictions
    // for the erased query explicitly.
    resetToEmpty();
  };
  const allOption = {
    id: 0,
    type: t('listingType.AllTypes'),
    icon: undefined as string | undefined
  };
  const typeOptions = [allOption, ...listingTypes];
  const selectedType = typeOptions.find(o => o.id === localType) || allOption;

  // Reset local type when modal opens
  useEffect(() => {
    if (isOpen) setLocalType(listingType);
  }, [isOpen]);
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
      setDropdownOpen(false);
      const timer = setTimeout(() => {
        autocompleteInput.current?.focus();
      }, 100);
      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.body.style.overflow = '';
        clearTimeout(timer);
      };
    }
  }, [isOpen, handleEscape, autocompleteInput]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  if (!isOpen) return null;
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  const handleSelectType = (option: typeof allOption) => {
    setLocalType(option.id);
    setDropdownOpen(false);
  };
  const handleSearch = () => {
    // If the user typed but didn't pick a suggestion, resolve the
    // highlighted/first one first, then run the search with its location.
    if (!selectActiveOrFirst(proceed)) proceed();
  };
  return <div className={styles.overlay} onClick={handleOverlayClick} data-sentry-component="SearchModal" data-sentry-source-file="SearchModal.tsx">
            <div className={styles.modal}>
                <div className={styles.searchArea} ref={containerRef}>
                <div className={styles.searchBar} ref={autocompleteContainer}>
                    <div className={styles.typeDropdownWrapper} ref={dropdownRef}>
                        <button type="button" className={styles.typeDropdown} onClick={() => setDropdownOpen(prev => !prev)}>
                            <span className={styles.typeLabel}>
                                {selectedType.type}
                            </span>
                            <ChevronDown size={16} data-sentry-element="ChevronDown" data-sentry-source-file="SearchModal.tsx" />
                        </button>
                        {dropdownOpen && <div className={styles.typeMenu}>
                                {typeOptions.map(option => <button key={option.id} type="button" className={styles.typeMenuItem} onClick={() => handleSelectType(option)}>
                                        {option.icon && <Image src={option.icon} alt="" width={18} height={18} unoptimized className={styles.typeMenuIcon} />}
                                        {option.type}
                                    </button>)}
                            </div>}
                    </div>

                    <div className={styles.divider} />

                    <button type="button" className={styles.locationBtn} onClick={getPermission} aria-label="Detect location">
                        <MapPin size={20} data-sentry-element="MapPin" data-sentry-source-file="SearchModal.tsx" />
                    </button>

                    <input ref={autocompleteInput} type="text" className={styles.searchInput} placeholder={t('landing-v4.searchPlaceholder')} autoCorrect="off" autoCapitalize="off" spellCheck={false} onInput={syncSearchValue} onChange={syncSearchValue} />

                    <button type="button" className={`${styles.clearBtn} ${hasSearchValue ? styles.clearBtnVisible : ''}`} onClick={handleClearSearch} aria-label={t('explore.clearSearch')} aria-hidden={!hasSearchValue} tabIndex={hasSearchValue ? 0 : -1}>
                        <X size={16} strokeWidth={2.25} data-sentry-element="X" data-sentry-source-file="SearchModal.tsx" />
                    </button>

                    <button type="button" className={styles.searchButton} onClick={handleSearch} aria-label="Search">
                        <Search size={20} color="#fff" data-sentry-element="Search" data-sentry-source-file="SearchModal.tsx" />
                    </button>
                </div>
                {suggestionsOpen && <SearchSuggestionsDropdown predictions={predictions} activeIndex={activeIndex} onHover={setActiveIndex} onSelect={selectByIndex} />}
                </div>
            </div>
        </div>;
};
export default SearchModal;